import { useEffect, useState } from "react";
import {
  ReportFilterBody,
  ReportFilterContainer,
  ReportHorizontalSeparator,
  ReportSectionHeading,
  MultiSelectButton,
  TransactionGrid,
} from "../ReportFilterPageStyles";
import { H3Heading } from "../../appSettings/AppSettingsStyles";
import { useNavigate } from "react-router-dom";
import { CustomButton, Loader, NavigateBack } from "../../../components";
import { useAppSelector } from "../../../_app";
import { Notify, Utils } from "../../../utils";
import { EmployeeService } from "../../../_service";
import { LoaderContainer } from "../../../CommonStyles";
import moment from "moment";
import ExcelJS from "exceljs";
import styled from "styled-components";

const ALL_COLUMNS = [
  "Employee Name",
  "Hourly",
  "Date Joined",
  "Resigned Date",
  "Employee ID",
  "Basic Salary",
  "Increment Amount",
  "Increment Percentage (%)",
];

const STATUS_OPTIONS = [
  { label: "All", value: "all" },
  { label: "Draft", value: "preview" },
  { label: "Confirmed", value: "finalized" },
  { label: "Executed", value: "executed" },
  { Label: "Sent to TellBoss", value: "sent_for_approval" },
];

const AnnualIncrementReport = () => {
  const navigate = useNavigate();
  const companyId = useAppSelector((state) => state.user.defaultCompnayId);
  const companyName = useAppSelector((state) => state.user.defaultCompany);

  const [isLoading, setIsLoading] = useState(false);
  const [listingData, setListingData] = useState<any[]>([]);
  const [statusFilter, setStatusFilter] = useState("all");
  const [yearFilter, setYearFilter] = useState<string>("all");
  const [selectedIds, setSelectedIds] = useState<any[]>([]);
  const [selectedColumns, setSelectedColumns] = useState<string[]>([...ALL_COLUMNS]);
  const [isExporting, setIsExporting] = useState(false);

  const yearOptions = ["all", ...Utils.getDefaultYearRange()];

  const fetchListingData = async () => {
    if (!companyId) return;
    setIsLoading(true);
    try {
      const res = await EmployeeService._getAnnualIncrementListing(companyId);
      if (res.status >= 200 && res.status < 300) {
        setListingData(res?.data?.data?.increments || []);
      }
    } catch (e: any) {
      Notify("Something went wrong", 0);
    }
    setIsLoading(false);
  };

  useEffect(() => {
    fetchListingData();
  }, [companyId]);

  const filteredData = listingData.filter((item: any) => {
    const matchesStatus = statusFilter === "all" || item.status === statusFilter;
    const matchesYear = yearFilter === "all" || !item.period_to || moment(item.period_to).format("YYYY") === yearFilter;
    return matchesStatus && matchesYear;
  });

  const toggleSelection = (id: any) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === filteredData.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(filteredData.map((item: any) => item.id));
    }
  };

  const toggleColumn = (col: string) => {
    setSelectedColumns((prev) =>
      prev.includes(col) ? prev.filter((x) => x !== col) : [...prev, col]
    );
  };

  const selectAllColumns = () => setSelectedColumns([...ALL_COLUMNS]);
  const selectNoneColumns = () => setSelectedColumns([]);

  const downloadExcel = async () => {
    if (selectedIds.length === 0 || selectedColumns.length === 0) return;
    setIsExporting(true);

    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Annual Increment Report");

      worksheet.addRow([companyName]);
      worksheet.addRow(["Annual Increment Report"]);
      worksheet.addRow([]);

      const headerRows = new Set([1, 2, 3]);
      let currentRow = 4;

      for (const id of selectedIds) {
        const increment = listingData.find((b: any) => b.id === id);
        if (!increment) continue;

        const res = await EmployeeService._getEmployeeAnnualIncrementEditData(id);
        if (res.status !== 200) continue;

        const records = res?.data?.data?.increment_records || [];

        const statusLabel = increment.status === "preview" ? "Draft" : increment.status === "finalized" ? "Confirmed" : increment.status === "executed" ? "Executed" : increment.status === "sent_for_approval" ? "Sent to TellBoss" : increment.status || "-";
        worksheet.addRow(["Increment Year:", moment(increment.period_to).format("DD-MM-YYYY")]);
        worksheet.getRow(currentRow).font = { bold: true, size: 11 };
        headerRows.add(currentRow);
        currentRow++;

        worksheet.addRow(["Effective Date:", increment.effective_date ? moment(increment.effective_date).format("DD-MM-YYYY") : "-"]);
        worksheet.getRow(currentRow).font = { bold: true, size: 11 };
        headerRows.add(currentRow);
        currentRow++;

        worksheet.addRow(["Status:", statusLabel]);
        worksheet.getRow(currentRow).font = { bold: true, size: 11 };
        headerRows.add(currentRow);
        currentRow++;

        worksheet.addRow(selectedColumns);
        worksheet.getRow(currentRow).font = { bold: true };
        headerRows.add(currentRow);
        currentRow++;

        let totalEmployee = 0;
        let totalSalary = 0;
        let totalIncrementAmount = 0;

        for (const emp of records) {
          const row = selectedColumns.map((col) => {
            switch (col) {
              case "Employee Name":
                return emp.employee_name || "";
              case "Hourly":
                return emp.salary_type || "";
              case "Date Joined":
                return emp.date_joined ? moment(emp.date_joined).format("DD-MM-YYYY") : "";
              case "Resigned Date":
                return emp.resigned_date ? moment(emp.resigned_date).format("DD-MM-YYYY") : "";
              case "Employee ID":
                return emp.employee || "";
              case "Basic Salary":
                return Utils.getCommanSepartedNumericStringWithDollar(
                  `${emp.basic_salary}`?.replace(/,/g, "")
                );
              case "Increment Amount":
                return Utils.getCommanSepartedNumericStringWithDollar(
                  `${emp.increment_amount}`.replace(/,/g, "")
                );
              case "Increment Percentage (%)":
                return `${(parseFloat(emp.increment_percentage || 0)).toFixed(2)}%`;
              default:
                return "";
            }
          });
          worksheet.addRow(row);
          currentRow++;

          totalEmployee++;
          totalSalary += parseFloat(`${emp.basic_salary}`?.replace(/,/g, "")) || 0;
          totalIncrementAmount += parseFloat(emp.increment_amount) || 0;
        }

        worksheet.addRow([]);
        currentRow++;

        const avgIncrementPct = totalSalary > 0 ? ((totalIncrementAmount / totalSalary) * 100).toFixed(2) : "0.00";

        const totalsRow = selectedColumns.map((col) => {
          switch (col) {
            case "Employee Name":
              return "TOTAL";
            case "Basic Salary":
              return Utils.getCommanSepartedNumericStringWithDollar(totalSalary.toFixed(2));
            case "Increment Amount":
              return Utils.getCommanSepartedNumericStringWithDollar(totalIncrementAmount.toFixed(2));
            case "Increment Percentage (%)":
              return `${avgIncrementPct}%`;
            default:
              return "";
          }
        });
        worksheet.addRow(totalsRow);
        worksheet.getRow(currentRow).font = { bold: true };
        currentRow++;

        worksheet.addRow([]);
        currentRow++;

        worksheet.addRow([]);
        currentRow++;
      }

      worksheet.getRow(1).font = { bold: true, size: 14 };
      worksheet.getRow(2).font = { bold: true };

      worksheet.columns.forEach((column, index) => {
        let maxLength = 0;
        if (column && typeof column.eachCell === "function") {
          column.eachCell({ includeEmpty: true }, (cell) => {
            const length = cell.value ? cell.value.toString().length : 0;
            if (length > maxLength) {
              maxLength = length;
            }
          });
        }
        column.width = Math.max(maxLength + 4, 18);
      });

      worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell, colNumber) => {
          if (colNumber > 1) {
            cell.alignment = { horizontal: "right" };
          }
        });
      });

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${companyName}_Annual_Increment_Report.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e: any) {
      Notify("Something went wrong while exporting", 0);
    }
    setIsExporting(false);
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "preview":
        return <p style={{ color: "#f4c104", fontWeight: "bold" }}>Draft</p>;
      case "finalized":
        return <p style={{ color: "orangered", fontWeight: "bold" }}>Confirmed</p>;
      case "executed":
        return <p style={{ color: "rgb(30, 81, 40)", fontWeight: "bold" }}>Executed</p>;
      case "sent_for_approval":
        return <p style={{ color: "rgb(30, 81, 40)", fontWeight: "bold" }}>Sent to TellBoss</p>;
      default:
        return status;
    }
  };

  if (isLoading) {
    return (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  }

  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: ".5rem",
        }}
      >
        <NavigateBack />
        <H3Heading>{Utils.getTitleWithCompanyName("Annual Increment Report")}</H3Heading>
      </div>

      <ReportFilterContainer>
        <ReportFilterBody>
          <div>
            <ReportSectionHeading>Filters</ReportSectionHeading>
            <div style={{ display: "flex", gap: 50, marginTop: 20 }}>
              <div style={{ display: "flex" }}>
                <Container>
                  <Text>Increment Status</Text>
                  <Select
                    value={statusFilter}
                    onChange={(e: any) => {
                      setStatusFilter(e.target.value);
                      setSelectedIds([]);
                    }}
                  >
                    {STATUS_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label || opt.Label}
                      </option>
                    ))}
                  </Select>
                </Container>
              </div>
              <div style={{ display: "flex" }}>
                <Container>
                  <Text>Increment Year</Text>
                  <Select
                    value={yearFilter}
                    onChange={(e: any) => {
                      setYearFilter(e.target.value);
                      setSelectedIds([]);
                    }}
                  >
                    {yearOptions.map((year) => (
                      <option key={year} value={year}>
                        {year === "all" ? "All" : year}
                      </option>
                    ))}
                  </Select>
                </Container>
              </div>
            </div>
          </div>

          <ReportHorizontalSeparator style={{ marginTop: 6 }} />

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <ReportSectionHeading>
              Increment Records ({selectedIds.length} selected)
            </ReportSectionHeading>
            <CustomButton
              width="200px"
              title={isExporting ? "Exporting..." : "Show Excel"}
              onClick={downloadExcel}
              disable={selectedIds.length === 0 || selectedColumns.length === 0 || isExporting}
            />
          </div>

          <div className="table" style={{ marginTop: "1rem" }}>
            <table className="company-table">
              <thead className="company-table-head">
                <tr>
                  <th style={{ textAlign: "center", width: "4%" }}>
                    <input
                      type="checkbox"
                      checked={selectedIds.length === filteredData.length && filteredData.length > 0}
                      onChange={toggleSelectAll}
                    />
                  </th>
                  <th style={{ textAlign: "left", width: "15%" }}>Increment Year</th>
                  <th style={{ textAlign: "center", width: "10%" }}>Increment Month</th>
                  <th style={{ textAlign: "right", width: "15%" }}>Total Salary</th>
                  <th style={{ textAlign: "right", width: "15%" }}>Total Increment</th>
                  <th style={{ textAlign: "right", width: "15%" }}>Average Increment (%)</th>
                  <th style={{ textAlign: "center", width: "15%" }}>Status</th>
                  <th style={{ textAlign: "center", width: "10%" }}>Action</th>
                </tr>
              </thead>
              <thead>
                {filteredData?.length > 0 &&
                  filteredData.map((item: any) => (
                    <tr key={item.id}>
                      <td style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          checked={selectedIds.includes(item.id)}
                          onChange={() => toggleSelection(item.id)}
                          style={{ width: 14, height: 14 }}
                        />
                      </td>
                      <td>{moment(item?.period_to).format("DD-MM-YYYY")}</td>
                      <td style={{ textAlign: "center" }}>
                        {item?.effective_date == null
                          ? "-"
                          : moment(item?.effective_date).format("DD-MM-YYYY")}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {"$" + Utils.getNumberWithCommas(item?.total_salary?.toFixed(2))}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {"$" + Utils.getNumberWithCommas(item?.total_increment?.toFixed(2))}
                      </td>
                      <td style={{ textAlign: "right" }}>
                        {item?.avg_increment_percentage?.toFixed(2)}%
                      </td>
                      <td style={{ textAlign: "center" }}>{getStatusLabel(item?.status)}</td>
                      <td style={{ textAlign: "center" }}>
                        <p
                          style={{
                            padding: "0.25rem 0.75rem",
                            background: "#03B381",
                            cursor: "pointer",
                            color: "white",
                            fontWeight: 400,
                            borderRadius: "0.25rem",
                            display: "inline-block",
                          }}
                          onClick={() => {
                            navigate("/dashboard/annual-increment/view", {
                              state: {
                                isEdit: false,
                                id: item?.id,
                                isFinalized: true,
                              },
                            });
                          }}
                        >
                          View
                        </p>
                      </td>
                    </tr>
                  ))}
              </thead>
            </table>
          </div>

          <ReportHorizontalSeparator style={{ marginTop: 20 }} />

          <div style={{ display: "flex", gap: 20, alignItems: "center" }}>
            <ReportSectionHeading>Columns to Export</ReportSectionHeading>
            <MultiSelectButton onClick={selectAllColumns}>All</MultiSelectButton>
            <MultiSelectButton onClick={selectNoneColumns}>None</MultiSelectButton>
          </div>

          <TransactionGrid style={{ marginTop: 12 }}>
            {ALL_COLUMNS.map((col) => (
              <div
                key={col}
                style={{
                  display: "flex",
                  gap: 20,
                  color: "#333333",
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedColumns.includes(col)}
                  onChange={() => toggleColumn(col)}
                />
                <p>{col}</p>
              </div>
            ))}
          </TransactionGrid>
        </ReportFilterBody>
      </ReportFilterContainer>
    </>
  );
};

export default AnnualIncrementReport;

const Container = styled.div`
  margin-bottom: 1rem;
  display: flex;
`;
const Text = styled.p`
  width: 130px;
  height: 30px;
  color: #fff;
  border: 1px solid #afb5ba;
  font-size: 0.8rem;
  background-color: #afb5ba;
  text-transform: none;
  display: flex;
  align-items: center;
  padding: 0 5px;
  border-radius: 3px 0 0 3px;
`;
const Select = styled.select`
  max-width: 180px;
  height: 30px;
  background-color: #087ca7;
  color: #fff;
  border-radius: 0 3px 3px 0;
  cursor: pointer;
  padding: 0 5px;
  text-align: center;
`;
